import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin } from 'lucide-react';
import { contactInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-brand-hero text-white">
      <div className="container mx-auto px-6 py-12">
        {/* Logo et description (Mobile first) */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <img 
              src="/logo.png" 
              alt="Chango's Renovation Logo" 
              className="h-12 md:h-16"
            />
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-white">CHANGO'S</h3>
              <p className="text-sm text-brand-gold font-semibold tracking-wide">RENOVATION</p>
            </div>
          </div>
          
          <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-2 italic">
            Donnez à votre projet la qualité qu'il mérite.
          </p>
          
          <p className="text-sm text-gray-400 leading-relaxed">
            Expert en construction et rénovation à Bruxelles. Fort de 15 ans d'expérience, nous transformons vos espaces avec professionnalisme et passion.
          </p>
        </div>

        {/* Icônes sociales (cercles) */}
        <div className="flex gap-4 mb-10">
          <a 
            href={`mailto:${contactInfo.email}`}
            className="w-12 h-12 rounded-full border-2 border-gray-500 hover:border-brand-gold flex items-center justify-center transition-all hover:bg-brand-gold/10"
            aria-label="Email"
          >
            <Mail size={20} className="text-gray-300" />
          </a>
          <a 
            href="#" 
            className="w-12 h-12 rounded-full border-2 border-gray-500 hover:border-brand-gold flex items-center justify-center transition-all hover:bg-brand-gold/10"
            aria-label="Instagram"
          >
            <Instagram size={20} className="text-gray-300" />
          </a>
          <a 
            href="#" 
            className="w-12 h-12 rounded-full border-2 border-gray-500 hover:border-brand-gold flex items-center justify-center transition-all hover:bg-brand-gold/10"
            aria-label="LinkedIn"
          >
            <Linkedin size={20} className="text-gray-300" />
          </a>
        </div>

        {/* Navigation et Contact en 2 colonnes sur mobile */}
        <div className="grid grid-cols-2 gap-8 mb-10">
          {/* Navigation */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-brand-gold uppercase tracking-wider">Navigation</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/services" className="text-sm text-gray-300 hover:text-white transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/realisations" className="text-sm text-gray-300 hover:text-white transition-colors">
                  Réalisations
                </Link>
              </li>
              <li>
                <Link to="/a-propos" className="text-sm text-gray-300 hover:text-white transition-colors">
                  À propos
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-gray-300 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-brand-gold uppercase tracking-wider">Contact</h3>
            <ul className="space-y-3">
              <li>
                <a href={`mailto:${contactInfo.email}`} className="text-sm text-gray-300 hover:text-white transition-colors break-all">
                  {contactInfo.email}
                </a>
              </li>
              <li>
                <a href={`tel:${contactInfo.phone}`} className="text-sm text-gray-300 hover:text-white transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li>
                <span className="text-sm text-gray-300">{contactInfo.location}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Desktop: 4 colonnes (masqué sur mobile) */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-8 mb-10">
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-brand-gold">Liens Rapides</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-gold rounded-full"></span>
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-gold rounded-full"></span>
                  Services
                </Link>
              </li>
              <li>
                <Link to="/realisations" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-gold rounded-full"></span>
                  Réalisations
                </Link>
              </li>
              <li>
                <Link to="/a-propos" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-gold rounded-full"></span>
                  À Propos
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-gold rounded-full"></span>
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-brand-gold">Nos Services</h3>
            <ul className="space-y-2">
              <li className="text-gray-300">Électricité & Domotique</li>
              <li className="text-gray-300">Aménagements Intérieurs</li>
              <li className="text-gray-300">Plomberie & Sanitaire</li>
              <li className="text-gray-300">Cuisine & Salle de bain</li>
              <li className="text-gray-300">Peinture & Décoration</li>
              <li className="text-gray-300">Dépannage & Urgences</li>
            </ul>
          </div>

          {/* Contact Desktop */}
          <div className="col-span-2">
            <h3 className="text-lg font-semibold mb-4 text-brand-gold">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-brand-gold mt-1 flex-shrink-0" />
                <a href={`tel:${contactInfo.phone}`} className="text-gray-300 hover:text-white transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-brand-gold mt-1 flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-gray-300 hover:text-white transition-colors break-all">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-brand-gold mt-1 flex-shrink-0" />
                <span className="text-gray-300">{contactInfo.location}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-brand-gold mt-1 flex-shrink-0" />
                <span className="text-gray-300">{contactInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-xs text-gray-400">
              © {currentYear} Chango's Renovation — Tous droits réservés.
            </p>
            <div className="flex gap-4 text-xs">
              <Link to="/mentions-legales" className="text-gray-500 hover:text-gray-300 transition-colors">
                Mentions légales
              </Link>
              <Link to="/politique-confidentialite" className="text-gray-500 hover:text-gray-300 transition-colors">
                Confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
